/** @module SiteAdmin/HomePage/Table */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"underscore",
	"csi/action",
	"csi/constants",
	"csi/def",
	"csi/locale",
	"csi/prefs/_base",
	"csi/reflection",
	"csi/store/Names",
	"csi/table/Preferences",
	"csi/table/Widget",
	"csi/utility/stringUtility",
	"csi/view/ToolbarPluginSelect",
	"csi/xml",
	"csi/xml/Query",
	"./TableToolbar",
	"./util"
], function(declare, lang, _, csiAction, constants, def, locale, csiPrefs, reflection, Names, TablePreferences, TableWidget, {stringSubstitute}, ToolbarPluginSelect, xml, Query, TableToolbar, HomePageUtil) {

	var ACTION_CHART = 'DashboardChart';
	var ACTION_FILTER = 'DashboardFilter';
	var ACTION_FILTER_INDEX = "2";
	var ACTION_TABLE = 'DashboardTable';

	var _Preferences = declare(TablePreferences, {
		/**
		 * Customize table custom view options on home page.
		 */
		constructor: function() {
			// set default table height to Medium, and remove option to hide chart as that will be available on toolbar
			this.setOptionDefaultValue("tableHeight", constants.TABLE_HEIGHT_MEDIUM);
			this.addCustomOption("chartHide", false);

			// remove options because no toolbar support
			this.removeCustomOption("suspended");
			this.removeCustomOption("suspendedRequireFilter");
			this.removeCustomOption("tableHideRoot");
			this.removeCustomOption("showCountIfSuspended");
			this.removeCustomOption("printAutoScale");
			this.removeCustomOption("printComments");
			this.removeCustomOption("printDynamicColumns");
			this.removeCustomOption("printFontScale");
			this.removeCustomOption("printFrozenColumnsForEachPage");
			this.removeCustomOption("printHeader");
			this.removeCustomOption("printOrientation");
			this.removeCustomOption("printPaper");
		}
	});

	const CONTEXT_PATHS_TO_HIERARCHY = {
		"Colorway": ["Child:${0}"],
		"Issue": ["Child:__Parent__/Child:Issue${0}"],
		"Order": ["Child:RealizedProduct/Child:__Parent__/Child:${0}"],
		"Sample": ["Child:__Parent__/Child:__Parent__/Child:__Parent__/Child:${0}"],
		"Style": ["Child:${0}"],
		"SupplierItem": ["Child:__Parent__/Child:__Parent__/Child:__Parent__/Child:${0}"],
		"SupplierRequest": [
			"Child:SR${0}",
			"Child:LineItems/Child:SRLineItemProduct/Child:${0}"
		],
		"TestRun": ["Child:__Parent__/Child:${0}"],
		"Test": ["Child:__Parent__/Child:__Parent__/Child:__Parent__/Child:__Parent__/Child:${0}"]
	};

	return declare(TableWidget, {
		allowFullHeight: false,
		allowSuspendInCustomViewPreview: true,
		prefClass: _Preferences,
		tableHeight: constants.TABLE_HEIGHT_MEDIUM,
		toolbarClass: "SiteAdmin/HomePage/TableToolbar",

		/** @property {string} [dashboardName] - dashboardName used when dashboard is not based on a type, like Season.
		  *  Added when extended HomePage panes to show all type instances across seasons. */
		dashboardName: null,

		/** @property {string} [dashboardState] - enum value for views that filter by State */
		dashboardState: null,

		/** @property {string} [dashboardType] - node type for views that filter by Type (should match getViewNodeType when specified) */
		dashboardType: null,

		/**
		 * @override
		 * Return dashboard-specific properties for preview
		 */
		getPreviewProperties() {
			const {dashboardName, dashboardState, dashboardType, defView} = this;
			const rc = reflection.getClass(defView.m_idType);
			const params = this.inherited(arguments);

			params.dashboardName = dashboardName;
			params.dashboardState = dashboardState;
			params.dashboardType = dashboardType;
			params.defView = def.getClassViewDef(rc, defView.m_id);
			params.defView.classController = defView.classController;

			return params;
		},

		/**
		 * Extension point to call when table's layout (filter) has changed.
		 * @param {Controller} ac - action controller
		 */
		onActionChangeHomePageLayout: function(ac) { },

		/**
		 * Extension point to call when table's width may have changed, which could force whole HomePage to resize.
		 */
		onActionChangeWidth: function() { },

		/**
		 * Populate homepage-specific toolbar.
		 * @param {Toolbar} tb
		 */
		onViewToolbar: function(tb) {
			if (csiPrefs.hasPersonalAccess()) {
				tb.set("allowToggle", true);
				tb.onToggleChart = lang.hitch(this, this._onToggleChart);
				tb.onToggleTable = lang.hitch(this, this._onToggleTable);
			}

			this.inherited(arguments);
		},

		/**
		 * Invoke filter action when it changes.
		 */
		_onActionFilter: function(ac) {
			if (this.dashboardState) {
				this.set('dashboardState', ac.actionValue);
			} else if (this.dashboardName) {
				this.set('dashboardName', ac.actionValue);
			} else {
				this.setViewNode(this.cacheView.get(ac.actionValue));
			}

			this.onActionChangeHomePageLayout(ac);
		},

		/**
		 * Toggle display of chart and redraw. If no chart, then define one.
		 * @param {boolean} value
		 */
		_onToggleChart: function(value) {
			// do nothing if still loading view to stored settings, or if current view setting already matches that of the incoming value;
			// we need to be careful though as "chartHide" could be false even when there is no chart (which effectively makes it hidden)
			var bHide = !value;
			var bHideOld = this.chartHide || !this.hasChart();

			if (this.isLoadingView() || (bHideOld == bHide)) {
				return;
			}

			var values = {chartHide: bHide};
			var params = {
				actionValue: values,
				onEnd: this.onActionChangeWidth
			};

			if (this.hasChart()) {
				// if view has chart, then just hide or show it; if hiding, then make sure table is shown.
				if (bHide && this.get("chartHideTable")) {
					values.chartHideTable = false;
				}

				csiAction.invoke(this, this.domNode, ACTION_CHART, params);
			} else if (!bHide) {
				// if view does not have a chart, then popup chart wizard to define one
				this.getPlugin("charts").onClick(null, null, params);
			}
		},

		/**
		 * Toggle display of table and redraw.
			 * @param {boolean} value
		 */
		_onToggleTable: function(value) {
			// do nothing if still loading view to stored settings
			var bHide = !value;

			if (this.isLoadingView() || (this.chartHideTable == bHide)) {
				return;
			}

			var values = {chartHideTable: bHide};
			var params = {actionValue: values};
			var callback;

			if (bHide && this.get("chartHide")) {
				values.chartHide = false;
				callback = this.redrawCharts;
			} else {
				callback = this.updateChartTableVisibility;
			}

			params.postProcessSuccess = callback.bind(this);

			csiAction.invoke(this, this.domNode, ACTION_TABLE, params);
		},

		/**
		 * Query for filter value as it will become root node of view.
		 */
		_postActionFilter: function(ac) {
			return this.refreshWidget();
		},

		/**
		 * Query for filter value as it will become root node of view.
		 */
		_preActionFilter: function(ac) {
			return xml.isValidURL(ac.actionValue) ? this.cacheView.getNodeDeferred(ac.actionValue) : true;
		},

		/**
		 * Configuration of the main query level
		 * @param {QueryLevel} ql
		 */
		onCreateMainQueryLevel: function(ql) {
			if (this.dashboardState) {
				var rc = this.defView.getPageClass();
				ql.serverQuery = new Query().Attr(HomePageUtil.getStateId(rc), xml.OP_EQUALS, this.dashboardState, HomePageUtil.getStatePath(rc));
			} else if (HomePageUtil.CONTEXT_TYPE_NAMES.includes(this.dashboardType) && this.dashboardName) {
				// If we need to get objects based on contextName across seasons.
				const {dashboardName, dashboardType} = this;
				const dashboardPath = dashboardType == "Season" && ql.type != "Issue" ? "ParentSeason" : dashboardType;
				const pathTemplates = CONTEXT_PATHS_TO_HIERARCHY[ql.type]; // targetType (Colorway, etc.)
				const q = new Query();

				if (dashboardName !== HomePageUtil.ALL_VALUE) {
					if (pathTemplates.length == 1) {
						q.Attr(xml.ATTR_NAME, xml.OP_EQUALS, dashboardName, stringSubstitute(pathTemplates[0], [dashboardPath]));
					} else {
						const qOr = q.Or();

						for (const path of pathTemplates) {
							const updatedPath = stringSubstitute(path, [dashboardPath]);
							qOr.Attr(xml.ATTR_NAME, xml.OP_EQUALS, dashboardName, updatedPath);
						}
					}
				}

				ql.serverQuery = q;
			}
		},

		/**
		 * Add action for custom view changes (chart/table toggle buttons).
		 */
		_resolveView: function() {
			this.defView._addViewAction(ACTION_CHART, {
				m_onAction: "onActionSetValue",
				m_postAction: "redrawCharts"
			});

			this.defView._addViewAction(ACTION_TABLE, {
				m_onAction: "onActionSetValue"
			});

			//Initialize widget id
			var id = this.defView.m_id;

			// initialize filter widget if necessary
			var filterLabel;
			var filterStore;
			var filterValue;

			const {
				dashboardName,
				dashboardState,
				dashboardType,
			} = this;

			if (dashboardState) {
				var rc = this.defView.getPageClass();
				filterLabel = HomePageUtil.getStateName(rc);
				filterStore = HomePageUtil.getStateStore(rc, true);
				filterValue = dashboardState;
			} else if (dashboardType == "ProductHierarchy" && (id.includes("Food") || id.includes("FormulatedMaterial"))) {
				filterLabel = locale.type(dashboardType);
				filterStore = HomePageUtil.getProductHierarchyValuesStore(id, dashboardType);
				filterValue = this.urlRoot;
			} else if (dashboardType) {
				if (dashboardName) {
					filterLabel = locale.type(dashboardType);
					filterStore = HomePageUtil.getContextValuesStore(id, dashboardType, dashboardName);
					filterValue = dashboardName;
				} else {
					filterLabel = locale.type(dashboardType);
					filterStore = Names.createFromType(dashboardType);
					filterValue = this.urlRoot;
				}
			}

			if (filterLabel && filterLabel != "User") {
				// insert before filter reset action
				this.plugins.splice(ACTION_FILTER_INDEX, 0, this.defView._addViewAction(ACTION_FILTER, {
					m_preAction: '_preActionFilter',
					m_onAction: '_onActionFilter',
					m_postAction: '_postActionFilter',
					classToolbarPlugin: ToolbarPluginSelect,
					classToolbarPluginParams: {
						showLabel: true,
						label: filterLabel,
						store: filterStore,
						value: filterValue
					}
				}));
			}

			this.inherited(arguments);
		}
	});
});
